import { useCallback, useRef, useState } from 'react';

/**
 * Builds the callback ref returned by {@link useElementWidth}.
 *
 * Kept outside the hook so the identity is trivially stable: it is created once
 * per chart instance and stored in a ref, never rebuilt on re-render.
 */
function createWidthRef(setWidth) {
  let detach = null;

  const attach = (node) => {
    if (detach) {
      detach();
      detach = null;
    }
    // Callers read `.current` for pointer maths, so the callback has to keep
    // behaving like an object ref as well.
    attach.current = node || null;
    if (!node) return;

    const measure = () => {
      // Ignore 0 (detached / display:none) so the chart keeps its last good
      // geometry instead of collapsing to nothing.
      const w = Math.round(node.clientWidth || node.getBoundingClientRect().width);
      if (w > 0) setWidth(w);
    };
    measure();

    // Older Safari / jsdom have no ResizeObserver; fall back to the window.
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', measure);
      detach = () => window.removeEventListener('resize', measure);
      return;
    }

    const ro = new ResizeObserver(measure);
    ro.observe(node);
    detach = () => ro.disconnect();
  };

  attach.current = null;
  return attach;
}

/**
 * Measure an element's content width so a chart can render its SVG at true
 * pixel size (viewBox === pixel box).
 *
 * Why not a fixed viewBox scaled by CSS: SVG font-size is expressed in user
 * units, so a fixed viewBox scales the text by (rendered width / viewBox
 * width). That is how the old charts ended up with ~5px axis labels — and why
 * simply enlarging the viewBox makes the text SMALLER, not bigger. Measuring
 * keeps one user unit equal to one CSS pixel at every breakpoint, so a stated
 * 11px label really is 11px on a phone and on a 4K monitor.
 *
 * A CALLBACK ref, not a mount effect, and that distinction is the whole point.
 * LineChart, PolarChart and HeatmapGrid all return a plain empty-state <div> on
 * their first render — which is every render before their data arrives — so the
 * element being measured does not exist yet. A `useEffect` that runs once at
 * mount therefore bound its observer to `null` and, having no reason to re-run,
 * never retried: `width` stayed at `fallback` for the life of the page. With
 * `viewBox="0 0 {fallback} h"` and `width="100%"`, the default
 * preserveAspectRatio then painted the plot at scale 1 centred in whatever card
 * it was given — a ~560px plot adrift in a 1,970px card. A callback ref fires
 * whenever the node actually appears, so the observer attaches the moment the
 * chart swaps from its empty state to a real plot.
 */
export function useElementWidth(fallback = 560) {
  const [width, setWidth] = useState(fallback);
  const refHolder = useRef(null);
  if (refHolder.current === null) {
    refHolder.current = createWidthRef(setWidth);
  }
  return [refHolder.current, width];
}

/**
 * Hover/focus state for a chart's tooltip.
 *
 * Tooltips enhance, they never gate: every value shown here is also reachable
 * from the axis and the selective direct labels. Focus behaves exactly like
 * hover so the keyboard path is not second-class.
 */
export function useChartTooltip() {
  const [tip, setTip] = useState(null); // { x, y, title, rows }
  const show = useCallback((payload) => setTip(payload), []);
  const hide = useCallback(() => setTip(null), []);
  return { tip, show, hide };
}

/**
 * Readable axis ticks: `count`+1 values from `min` to a rounded ceiling, so
 * gridlines land on 2,000 rather than 8,333.33.
 *
 * `min` defaults to 0, which is what every counter and rate chart wants. Pass a
 * negative floor for a signed measure — a prediction bias, or the regime
 * corrector's offset — where the zero line is the whole point and an axis that
 * starts at 0 simply hides half the data.
 */
export function niceTicks(max, count = 4, min = 0) {
  if (!(max > min)) return [0, 1];
  const rawStep = (max - min) / count;
  const mag = Math.pow(10, Math.floor(Math.log10(rawStep)));
  const norm = rawStep / mag;
  const step = (norm <= 1 ? 1 : norm <= 2 ? 2 : norm <= 2.5 ? 2.5 : norm <= 5 ? 5 : 10) * mag;
  const bottom = Math.floor(min / step) * step;
  const top = Math.ceil(max / step) * step;
  // Index multiplication rather than repeated addition: accumulating a float
  // step from a negative floor drifts, and a tick landing on -0.30000000000000004
  // is a visibly wrong gridline label.
  const steps = Math.round((top - bottom) / step);
  const out = [];
  for (let i = 0; i <= steps; i++) out.push(bottom + i * step);
  return out;
}

/** Left gutter wide enough for the widest y label, so ticks never clip. */
export function axisGutter(ticks, format) {
  const longest = ticks.reduce((n, t) => Math.max(n, String(format(t)).length), 1);
  return Math.max(34, 12 + longest * 7);
}

/** Series colour + dash for slot n (1-based). Dash carries identity in Mono. */
export function seriesStyle(slot) {
  const n = ((slot - 1) % 6) + 1;
  return {
    color: `var(--chart-s${n})`,
    dash: `var(--chart-dash-${n})`,
  };
}
